import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    CircularProgress,
    Alert,
    Grid,
    Card,
    Typography,
    Avatar,
    Chip,
    TextField,
    InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from '../config/axiosConfig';
import TitleAndInfo from './TitleAndInfo';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const HedgeFunds = () => {
    const theme = useTheme();
    const [hedgeFunds, setHedgeFunds] = useState([]);
    const [filteredHedgeFunds, setFilteredHedgeFunds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const titleTop = "Top Hedge Funds";
    const title = "";
    const info = "Track and analyze the world's most influential hedge funds and institutional investors. From Warren Buffett's Berkshire Hathaway to Cathie Wood's ARK Invest.";

    useEffect(() => {
        fetchHedgeFunds();
    }, []);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredHedgeFunds(hedgeFunds);
        } else {
            const filtered = hedgeFunds.filter((fund) => {
                const nameMatch = fund.name?.toLowerCase().includes(searchTerm.toLowerCase());
                const managerMatch = fund.manager?.toLowerCase().includes(searchTerm.toLowerCase());
                return nameMatch || managerMatch;
            });
            setFilteredHedgeFunds(filtered);
        }
    }, [searchTerm, hedgeFunds]);

    const fetchHedgeFunds = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/hedge-funds');
            
            if (response.data && response.data.data) {
                setHedgeFunds(response.data.data);
                setFilteredHedgeFunds(response.data.data);
            }
            setError(null);
        } catch (error) {
            console.error('Error fetching hedge funds:', error);
            setError('Failed to fetch hedge funds data');
        } finally {
            setLoading(false);
        }
    };

    // Format fund name to proper case
    const formatFundName = (name) => {
        if (!name) return 'Unknown Fund';
        return name.toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Format large numbers to include B/M/T suffix
    const formatCurrency = (value) => {
        if (value === undefined || value === null || value === 0) return 'N/A';
        if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
        if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
        if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
        if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
        return `$${value.toString()}`;
    };

    // Get category color
    const getCategoryColor = (category) => {
        const colors = {
            'Legendary': '#FFD700',
            'Index Fund': '#4CAF50',
            'Growth': '#FF9800',
            'Tech': '#2196F3',
            'Activist': '#F44336',
            'Quant': '#9C27B0',
            'Value': '#795548',
            'Multi-Strategy': '#607D8B',
            'Macro': '#3F51B5',
            'ESG': '#8BC34A',
            'Event-Driven': '#E91E63'
        };
        return colors[category] || '#757575';
    };

    if (loading) {
        return (
            <Container maxWidth="xl">
                <TitleAndInfo titleTop={titleTop} title={title} info={info} />
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl">
                <TitleAndInfo titleTop={titleTop} title={title} info={info} />
                <Alert severity="error">
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <TitleAndInfo titleTop={titleTop} title={title} info={info} />

            {/* Search Bar */}
            <Box sx={{ mb: 4 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by fund name or manager..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        maxWidth: '400px',
                    }}
                />
                {searchTerm && (
                    <Typography sx={{ mt: 1, color: 'text.secondary' }}>
                        Found {filteredHedgeFunds.length} result{filteredHedgeFunds.length !== 1 ? 's' : ''}
                    </Typography>
                )}
            </Box>

            {filteredHedgeFunds && filteredHedgeFunds.length > 0 ? (
                <>
                    {/* Hedge Funds Grid */}
                    <Grid container spacing={3}>
                        {filteredHedgeFunds.map((fund) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={fund.cik}>
                                <RouterLink 
                                    to={`/hedge-fund/${fund.cik}`} 
                                    style={{ textDecoration: 'none' }}
                                >
                                    <Card 
                                        sx={{ 
                                            p: 3, 
                                            height: '100%', 
                                            display: 'flex', 
                                            flexDirection: 'column',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: 6
                                            },
                                            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
                                            borderRadius: 2,
                                            position: 'relative',
                                            overflow: 'visible'
                                        }}
                                    >
                                        {/* Category Chip */}
                                        <Chip
                                            label={fund.category || 'Hedge Fund'}
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: 12,
                                                right: 12,
                                                bgcolor: getCategoryColor(fund.category),
                                                color: 'white',
                                                fontWeight: 'bold',
                                                fontSize: '0.7rem'
                                            }}
                                        />

                                        {/* Fund Info */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, mt: 1 }}>
                                            <Avatar 
                                                sx={{ 
                                                    width: 72, 
                                                    height: 72, 
                                                    mb: 2,
                                                    background: `linear-gradient(135deg, ${getCategoryColor(fund.category)}88, ${getCategoryColor(fund.category)}44)`
                                                }}
                                            >
                                                <Typography variant="h4" color="white" fontWeight="bold">
                                                    {fund.name ? fund.name.charAt(0) : '?'}
                                                </Typography>
                                            </Avatar>
                                            
                                            <Typography variant="h6" align="center" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                {formatFundName(fund.name)}
                                            </Typography>
                                            
                                            {fund.manager && (
                                                <Typography variant="body2" color="primary" align="center" sx={{ mb: 1, fontStyle: 'italic' }}>
                                                    {fund.manager}
                                                </Typography>
                                            )}
                                            
                                            <Typography variant="subtitle1" color="text.secondary" align="center" fontWeight="bold">
                                                AUM: {formatCurrency(fund.marketValue)}
                                            </Typography>
                                        </Box>

                                        {/* Metrics */}
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            mt: 'auto',
                                            mb: 2
                                        }}>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography variant="h6" fontWeight="bold">
                                                    {fund.numberOfHoldings || 0}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Holdings
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography variant="h6" fontWeight="bold">
                                                    {typeof fund.turnover === 'number' ? (fund.turnover * 100).toFixed(1) + '%' : 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Turnover
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Performance */}
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            pt: 2,
                                            borderTop: `1px solid ${theme.palette.divider}`
                                        }}>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography 
                                                    variant="h6" 
                                                    fontWeight="bold"
                                                    color={
                                                        typeof fund.performancePercentage1year === 'number' 
                                                        ? (fund.performancePercentage1year >= 0 ? 'success.main' : 'error.main')
                                                        : 'text.secondary'
                                                    }
                                                >
                                                    {typeof fund.performancePercentage1year === 'number' 
                                                        ? `${fund.performancePercentage1year >= 0 ? '+' : ''}${fund.performancePercentage1year.toFixed(1)}%` 
                                                        : 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    1-Year Return
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                                    {fund.dataYear ? `${fund.dataYear} Q${fund.dataQuarter}` : 'No Data'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Last Updated
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </RouterLink>
                            </Grid>
                        ))}
                    </Grid>
                </>
            ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary">
                        {searchTerm ? 'No hedge funds found matching your search.' : 'No hedge funds found.'}
                    </Typography>
                </Box>
            )}
        </Container>
    );
};

export default HedgeFunds;